const allReflectedAttrs = [
  "autocapitalize",
  "class",
  "draggable",
  "hidden",
  "id",
  "itemprop",
  "lang",
  "role",
  "slot",
  "spellcheck",
  "style",
  "tabindex",
  "title",
  "translate",
  "autocomplete",
  "autofocus",
  "disabled",
  "form",
  "list",
  "max",
  "maxlength",
  "minlength",
  "min",
  "multiple",
  "pattern",
  "placeholder",
  "readonly",
  "required",
  "size",
  "type",
  //   "value", removed because special handling in monitor props
];

const allConfigProps = [
  "altFormat",
  "altInput",
  "altInputClass",
  "allowInput",
  "allowInvalidPreload",
  "ariaDateFormat",
  "conjunction",
  "clickOpens",
  "dateFormat",
  "defaultDate",
  "defaultHour",
  "defaultMinute",
  "disable",
  "disableMobile",
  "enable",
  "enableTime",
  "enableSeconds",
  "hourIncrement",
  "inline",
  "maxDate",
  "minDate",
  "minuteIncrement",
  "mode",
  "nextArrow",
  "noCalendar",
  "position",
  "prevArrow",
  "shorthandCurrentMonth",
  "static",
  "showMonths",
  "time_24hr",
  "weekNumbers",
  "wrap",
  "monthSelectorType",
];
const isWritable = (obj, prop) => Object.getOwnPropertyDescriptor(obj, prop)?.writable;

// Creates a local-time Date whose calendar date matches the UTC date of the given posix
// timestamp. FlatPickr renders Date objects in local time, so this ensures the displayed
// date is always the UTC date regardless of the browser's timezone.
const posixToLocalDate = (posix) => {
  const d = new Date(Number(posix));
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

const init = () => {
  customElements.define(
    "avt-flatpickr",
    class extends HTMLElement {
      static get observedAttributes() {
        return [...allReflectedAttrs, "value"];
      }
      constructor() {
        super();
      }
      connectedCallback() {
        // init calendar
        if (!this.calendar) {
          // Create Nodes
          this.$input = document.createElement("input");
          this.$wrapper = document.createElement("div");

          // Add CSS selector to $wrapper
          this.$wrapper.classList.add("avt-flatpickr-wrapper");
          // Apply valid attributes to the input
          [...this.attributes].reduce(
            (acc, cur) => {
              if (allReflectedAttrs.includes(cur.name)) acc.setAttribute(cur.name, cur.value);
              return acc;
            },
            this.$input,
          );

          // Append Nodes
          this.appendChild(this.$input);
          this.appendChild(this.$wrapper);
          // default config for this type of flatpickr initialization.
          const customElementFlatpickrConfig = {
            positionElement: this.$input,
            appendTo: this.$wrapper,
            onChange: (selectedDates) => {
              this.posixStrings = selectedDates
                ? selectedDates
                    .map((date) =>
                      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
                    )
                    .join("|")
                : [];
            },
            // unusedConfigOptions: [onClose, onOpen, onReady, formatDate, parseDate]
          };
          // Init FlatPickr
          this.calendarProps = allConfigProps.reduce(
            (acc, cur) => (this[cur] !== undefined ? { ...acc, [cur]: this[cur] } : acc),
            customElementFlatpickrConfig,
          );
          this.$input.addEventListener("keydown", (e) => {
            if (!this.allowClearing && (e.key === "Backspace" || e.key === "Delete")) {
              e.preventDefault();
              e.stopImmediatePropagation();
            }
          });

          this.calendar = window.flatpickr(this.$input, this.calendarProps);

          // Allow Dynamic Configs
          const monitorProps = () => {
            allConfigProps.forEach((prop) => {
              if (this.calendarProps[prop] !== this[prop]) {
                // Update Active Config (May trigger redraw)
                this.calendar.set(prop, this[prop]);
                // Update init/mutable config reference.
                this.calendarProps[prop] = this[prop];
              }
            });

            allReflectedAttrs.forEach((prop) => {
              if (this.$input[prop] !== this[prop]) {
                if (isWritable(this, prop) || isWritable(this.$input, prop)) {
                  // Update Input IDL Props (https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes#content_versus_idl_attributes)
                  this.$input[prop] = this[prop];
                }
              }
            });

            if (this.prevValue !== this.value) {
              const newDates = this.value
                ? this.value.split("|").map(posixToLocalDate)
                : [];
              this.calendar.setDate(newDates);
              this.prevValue = this.value;
            }
            requestAnimationFrame(monitorProps);
          };
          // .destroy() removes event handlers, once handlers are removed
          // this list will be empty and monitoring will stop.
          this.calendar._handlers && monitorProps();
        }
      }
      disconnectedCallback() {
        this.calendar.destroy();
      }
      attributeChangedCallback(name, oldValue, newValue) {
        if (name === "value") {
          this.calendar.setDate(
            newValue ? newValue.split("|").map(posixToLocalDate) : [],
          );
          return;
        }
        if (this.$input && name === "size" && oldValue != newValue) {
          console.log(this.$input);
          this.$input.size = newValue;
          return;
        }
        if (this.$input && newValue !== oldValue) {
          newValue === undefined
            ? this.$input.setAttribute(name, newValue)
            : this.$input.hasAttribute(name) && this.$input.removeAttribute(name);
        }
      }
    },
  );
};

export default { init };
